import { useAppSelector } from '@hooks/useRedux';
import { AccessControlCode } from '@shared/constants';
import {
  selectCurrentUser,
  selectHasUnrestrictedAccess,
} from '@store/auth/authSelectors';
import {
  BarChart2,
  BookMarked,
  Building2,
  Grid2X2,
  MoonStar,
  TrendingUp,
} from 'lucide-react';
import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';

interface ReportNavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  requiredCode: AccessControlCode;
}

const REPORT_NAV: ReportNavItem[] = [
  {
    to: '/accommodation/reports/revenue',
    label: 'Revenue',
    icon: <BarChart2 size={14} />,
    requiredCode: AccessControlCode.ACCOMMODATION_REPORTS_REVENUE,
  },
  {
    to: '/accommodation/reports/occupancy',
    label: 'Occupancy',
    icon: <Building2 size={14} />,
    requiredCode: AccessControlCode.ACCOMMODATION_REPORTS_OCCUPANCY,
  },
  {
    to: '/accommodation/reports/booking-source',
    label: 'Booking Sources',
    icon: <BookMarked size={14} />,
    requiredCode: AccessControlCode.ACCOMMODATION_REPORTS_BOOKING_SOURCE,
  },
  {
    to: '/accommodation/reports/night-audit',
    label: 'Night Audit',
    icon: <MoonStar size={14} />,
    requiredCode: AccessControlCode.ACCOMMODATION_REPORTS_NIGHT_AUDIT,
  },
  {
    to: '/accommodation/reports/room-revenue',
    label: 'Room Revenue',
    icon: <Grid2X2 size={14} />,
    requiredCode: AccessControlCode.ACCOMMODATION_REPORTS_ROOM_REVENUE,
  },
  {
    to: '/accommodation/reports/revpar',
    label: 'RevPAR',
    icon: <TrendingUp size={14} />,
    requiredCode: AccessControlCode.ACCOMMODATION_REPORTS_REVPAR,
  },
];

export const AccommodationReportsLayout: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const hasUnrestrictedAccess = useAppSelector(selectHasUnrestrictedAccess);
  const userCodes: string[] = (currentUser as any)?.access_control_codes ?? [];

  // Super/Admin users bypass per-code checks — they have unrestricted access
  const visible = REPORT_NAV.filter(
    (item) => hasUnrestrictedAccess || userCodes.includes(item.requiredCode),
  );

  return (
    <div className="space-y-4">
      {/* Report sub-navigation */}
      <div className="rounded-2xl border border-gray-200 bg-white px-5 pt-4">
        <div className="flex flex-wrap gap-6">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  'flex items-center gap-1.5 border-b-2 pb-4 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-pos-primary text-pos-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-900',
                ].join(' ')
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Active report content */}
      <Outlet />
    </div>
  );
};
